import React, { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton, Typography, Modal, Box, Button } from '@mui/material';
import dayjs from 'dayjs';
import { addDoc, collection, updateDoc } from 'firebase/firestore';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { negotiation as negotiationDoc } from '../../api/firebase/firestore/negotiation';
import { orderSchema } from '../../api/firebase/firestore/negotiation/validation';
import { sendNoticeMail } from '../../api/sendgrid';
import { compensationOrderChoice } from '../../constants/CheckboxList';
import {
  black,
  blueButton,
  primaryButtonBackgroundColor,
  tertiaryButtonBackground
} from '../../constants/Colors';
import { chatType, negotiationStatus } from '../../constants/enum';
import { throwError } from '../../modules/error';

import CustomCheckbox from './CustomCheckbox';
import CustomDatePicker from './CustomDatePicker';
import CustomRadioButton from './CustomRadioButton';
import CustomTextfield from './CustomTextfield';

import type {
  NegotiationType,
  OrderType
} from '../../api/firebase/firestore/negotiation/negotiationType';
import type { User } from 'firebase/auth';

const styles = {
  blueButton,
  modal: {
    '@media screen and (max-width:480px)': {
      margin: '0 16px'
    },
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    p: 2
  },
  paper: {
    '@media screen and (max-width:480px)': {
      padding: '24px 16px',
      width: '100%'
    },
    backgroundColor: 'white',
    maxHeight: '90%',
    overflowY: 'auto',
    padding: '24px 0',
    position: 'relative',
    width: '480px'
  },
  primaryButtonBackgroundColor,
  tertiaryButtonBackground
};

interface Props {
  open: boolean;
  handleClose: () => void;
  negotiation: NegotiationType | null;
  currentUser: User | null | undefined;
  init: () => Promise<void>;
  handleChangeSubmit: () => void;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const InsertOrderModal = (props: Props): React.ReactElement => {
  const { t } = useTranslation();
  const {
    open,
    handleClose,
    currentUser,
    negotiation,
    init,
    handleChangeSubmit,
    setLoading
  } = props;
  const [firstSubmit, setFirstSubmit] = useState(false);

  const {
    control,
    setValue,
    register,
    clearErrors,
    watch,
    formState: { errors },
    handleSubmit
  } = useForm<OrderType>({
    defaultValues: {
      publishEndDate: null,
      publishStartDate: null,
      reportSubmissionDate: null
    },
    resolver: yupResolver(orderSchema)
  });

  const checkboxArray = (value: boolean[]): number[] => {
    const array: number[] = [];
    value.forEach((elm: boolean, key) => {
      if (elm) {
        array.push(key);
      }
    });
    return array;
  };

  const getCompensationsString = (): string =>
    checkboxArray(watch('compensation'))
      .map((elm) => {
        if (elm === 1) {
          return t('message.order.compensation.1');
        } else if (elm === 2) {
          return t('message.order.compensation.2');
        } else if (elm === 3) {
          return t('message.order.compensation.3');
        } else if (elm === 4) {
          return t('message.order.compensation.4');
        } else if (elm === 5) {
          return t('message.order.compensation.5');
        } else if (elm === 6) {
          return t('message.order.compensation.6');
        }
        return '';
      })
      .join('\n');

  const onSubmit = async (): Promise<void> => {
    setLoading(true);
    try {
      const negotiationDocument = negotiationDoc(
        negotiation?.negotiationId ?? ''
      );

      await updateDoc(negotiationDocument, {
        status: negotiationStatus.order,
        updated: new Date()
      });

      const orderChat = `発注内容
【発注主名】${watch('companyName')}
【受注広告商品/パッケージ名】${watch('pkgName')}
【受注金額】${Number(watch('amount')).toLocaleString()}
【握ってるKPI】${watch('kpi')}
【二次利用やデータ利用などオプションの有無】${
        watch('optionFlg') === 'true' ? 'あり' : 'なし'
      }
【レポートの有無】${watch('reportFlg') === 'true' ? 'あり' : 'なし'}
【レポートの提出予定日】${
        watch('reportSubmissionDate')
          ? dayjs(watch('reportSubmissionDate')).format('YYYY/MM/DD')
          : 'なし'
      }
【特記事項】\n${getCompensationsString()}

メディアへの支払い方法
【振込タイミング】${watch('transferTiming') === 'true' ? '前払い' : '後払い'}
【振込期日】${watch('depositTiming')}`;
      const notice = await addDoc(collection(negotiationDocument, 'chats'), {
        chat: '発注内容が入力されました',
        created: new Date(),
        senderId: currentUser?.uid ?? '',
        type: chatType.system
      });

      await updateDoc(notice, {
        chatId: notice.id
      });

      const chat = await addDoc(collection(negotiationDocument, 'chats'), {
        chat: orderChat,
        created: new Date(),
        senderId: currentUser?.uid ?? '',
        type: chatType.orderDetail
      });

      await updateDoc(chat, {
        chatId: chat.id
      });

      const orderData = {
        amount: Number(watch('amount')),
        chatId: chat.id,
        companyName: watch('companyName'),
        compensation: checkboxArray(watch('compensation')),
        created: new Date(),
        depositTiming: watch('depositTiming'),
        kpi: watch('kpi'),
        optionContent: watch('optionContent') || '',
        optionFlg: convertBooleanToNumber(watch('optionFlg')),
        pkgName: watch('pkgName'),
        publishEndDate: watch('publishEndDate')?.toDate() ?? null,
        publishStartDate: watch('publishStartDate')?.toDate() ?? null,
        reportFlg: convertBooleanToNumber(watch('reportFlg')),
        reportSubmissionDate: watch('reportSubmissionDate')?.toDate() ?? null,
        senderId: currentUser?.uid ?? '',
        transferTiming: convertBooleanToNumber(watch('transferTiming'))
      };

      const order = await addDoc(
        collection(negotiationDocument, 'orders'),
        orderData
      );

      await updateDoc(order, {
        orderId: order.id
      });

      if (negotiation?.agentId) {
        sendNoticeMail(
          negotiation?.agentMainUid ?? '',
          negotiation?.agentMember ?? [],
          orderChat,
          negotiation,
          '発注内容',
          currentUser?.uid ?? ''
        );
      } else if (negotiation?.clientId) {
        sendNoticeMail(
          negotiation?.clientMainUid ?? '',
          negotiation?.clientMember ?? [],
          orderChat,
          negotiation,
          '発注内容',
          currentUser?.uid ?? ''
        );
      }

      await init();
      handleChangeSubmit();
    } catch (error) {
      throwError(error);
    } finally {
      handleClose();
      setLoading(false);
    }
  };

  useEffect(() => {
    if (watch('optionFlg') === 'false') {
      clearErrors('optionContent');
    }
  }, [watch('optionFlg')]);

  useEffect(() => {
    if (watch('reportFlg') === 'false') {
      clearErrors('reportSubmissionDate');
    }
  }, [watch('reportFlg')]);

  function convertBooleanToNumber(value: string | boolean): number {
    return value === 'true' ? 1 : 2;
  }

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('companyName') === undefined ||
        '' ||
        watch('pkgName') === undefined ||
        '' ||
        watch('amount') === undefined ||
        '' ||
        watch('kpi') === undefined ||
        '' ||
        watch('optionFlg') === undefined ||
        '' ||
        watch('reportFlg') === undefined ||
        '' ||
        watch('compensation') === undefined ||
        '' ||
        watch('depositTiming') === undefined ||
        '' ||
        Boolean(errors.companyName) ||
        Boolean(errors.pkgName) ||
        Boolean(errors.amount) ||
        Boolean(errors.publishStartDate) ||
        Boolean(errors.publishEndDate) ||
        Boolean(errors.kpi) ||
        Boolean(errors.optionFlg) ||
        Boolean(errors.optionContent) ||
        Boolean(errors.reportFlg) ||
        Boolean(errors.reportSubmissionDate) ||
        Boolean(errors.compensation) ||
        Boolean(errors.depositTiming)
      );
    }
    return false;
  };

  return (
    <>
      <Modal open={open} onClose={handleClose} sx={styles.modal}>
        <Typography sx={styles.paper}>
          <Typography
            sx={{
              '@media screen and (max-width:480px)': {
                p: 0
              },
              p: '0 40px'
            }}
          >
            <IconButton
              edge="end"
              color="inherit"
              aria-label="close"
              onClick={handleClose}
              style={{
                color: '#757575',
                position: 'absolute',
                right: '40px',
                top: '24px'
              }}
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ color: black, fontSize: '22px', mb: '24px' }}>
              {t('message.insertOrderTitle')}
            </Typography>
            <Controller
              render={({ field }) => (
                <CustomTextfield
                  {...field}
                  error={Boolean(errors.companyName)}
                  helperText={errors.companyName?.message}
                  labelText={t('message.order.companyName')}
                  requiredText={t('form.required')}
                  isRequired
                  placeholder={t('form.insert')}
                  width={'article'}
                />
              )}
              control={control}
              name="companyName"
            />
            <Controller
              render={({ field }) => (
                <CustomTextfield
                  {...field}
                  error={Boolean(errors.pkgName)}
                  helperText={errors.pkgName?.message}
                  labelText={t('message.order.pkgName')}
                  requiredText={t('form.required')}
                  isRequired
                  placeholder={t('form.insert')}
                  width={'article'}
                />
              )}
              control={control}
              name="pkgName"
            />
            <Controller
              render={({ field }) => (
                <CustomTextfield
                  {...field}
                  error={Boolean(errors.amount)}
                  helperText={errors.amount?.message}
                  labelText={t('message.order.amount')}
                  requiredText={t('form.required')}
                  isRequired
                  placeholder={t('form.insert')}
                  width={'article'}
                />
              )}
              control={control}
              name="amount"
            />
            <Typography sx={{ alignItems: 'center', display: 'flex' }}>
              <Controller
                render={({ field }) => (
                  <CustomDatePicker
                    {...field}
                    isRequired={false}
                    error={Boolean(errors.publishStartDate)}
                    helperText={errors.publishStartDate?.message}
                    labelText={t('message.order.publishDate')}
                    placeholder="2022/07/24"
                    disabled={false}
                    width={'100%'}
                    twoComponent
                    setValue={(newValue) => {
                      setValue('publishStartDate', newValue);
                    }}
                    onChangeForValide={field.onChange}
                  />
                )}
                control={control}
                name="publishStartDate"
              />
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                  mb: '24px',
                  mt: '22px',
                  mx: 1
                }}
              >
                <Typography variant="body1" component="div" sx={{ mb: '20px' }}>
                  〜
                </Typography>
              </Box>
              <Controller
                render={({ field }) => (
                  <CustomDatePicker
                    {...field}
                    isRequired={false}
                    error={Boolean(errors.publishEndDate)}
                    helperText={errors.publishEndDate?.message}
                    labelText={'\u3000'}
                    placeholder="2022/07/24"
                    twoComponent
                    width={'100%'}
                    disabled={false}
                    setValue={(newValue) => {
                      setValue('publishEndDate', newValue);
                    }}
                    onChangeForValide={field.onChange}
                  />
                )}
                control={control}
                name="publishEndDate"
              />
            </Typography>
            <Controller
              render={({ field }) => (
                <CustomTextfield
                  {...field}
                  error={Boolean(errors.kpi)}
                  helperText={errors.kpi?.message}
                  labelText={t('message.order.kpi')}
                  requiredText={t('form.required')}
                  isRequired
                  placeholder={t('form.insert')}
                  width={'article'}
                />
              )}
              control={control}
              name="kpi"
            />
            <Controller
              render={({ field }) => (
                <CustomRadioButton
                  {...field}
                  error={Boolean(errors.optionFlg)}
                  helperText={errors.optionFlg?.message}
                  labelText={t('message.order.optionFlg')}
                  requiredText={t('form.required')}
                  isRequired
                  trueOption={t('message.order.ari')}
                  falseOption={t('message.order.nashi')}
                />
              )}
              control={control}
              name="optionFlg"
            />
            {watch('optionFlg') === 'true' && (
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.optionContent)}
                    helperText={errors.optionContent?.message}
                    labelText={t('message.order.optionContent')}
                    requiredText={t('form.required')}
                    isRequired
                    placeholder={t('form.xxx')}
                    width={'article'}
                  />
                )}
                control={control}
                name="optionContent"
              />
            )}
            <Controller
              render={({ field }) => (
                <CustomRadioButton
                  {...field}
                  error={Boolean(errors.reportFlg)}
                  helperText={errors.reportFlg?.message}
                  labelText={t('message.order.reportFlg')}
                  requiredText={t('form.required')}
                  isRequired
                  trueOption={t('message.order.ari')}
                  falseOption={t('message.order.nashi')}
                />
              )}
              control={control}
              name="reportFlg"
            />
            {watch('reportFlg') === 'true' && (
              <Controller
                render={({ field }) => (
                  <CustomDatePicker
                    {...field}
                    isRequired
                    requiredText={t('form.required')}
                    error={Boolean(errors.reportSubmissionDate)}
                    helperText={errors.reportSubmissionDate?.message}
                    labelText={t('message.order.reportSubmissionDate')}
                    placeholder="2022/07/24"
                    disabled={false}
                    width={'50%'}
                    setValue={(newValue) => {
                      setValue('reportSubmissionDate', newValue);
                    }}
                    onChangeForValide={field.onChange}
                  />
                )}
                control={control}
                name="reportSubmissionDate"
              />
            )}
            <CustomCheckbox
              labelText={t('message.order.compensation.label')}
              description={t('message.order.compensation.description')}
              isRequired
              requiredText={t('form.required')}
              list={compensationOrderChoice()}
              control={control}
              registerOrder={register}
              watchOrder={watch}
              error={Boolean(errors.compensation)}
              helperText={errors.compensation?.message}
              formMb="narrow"
              name="compensationOrder"
            />
            <Typography
              sx={{ color: black, fontSize: '14px', mb: '8px', mt: '24px' }}
            >
              {t('message.order.howToPay')}
            </Typography>
            <Controller
              render={({ field }) => (
                <CustomRadioButton
                  {...field}
                  error={Boolean(errors.transferTiming)}
                  helperText={errors.transferTiming?.message}
                  isRequired
                  requiredText={t('form.required')}
                  labelText={t('message.order.transferTiming')}
                  trueOption={t('message.order.before')}
                  falseOption={t('message.order.after')}
                />
              )}
              control={control}
              name="transferTiming"
            />
            <Controller
              render={({ field }) => (
                <CustomTextfield
                  {...field}
                  error={Boolean(errors.depositTiming)}
                  helperText={errors.depositTiming?.message}
                  labelText={t('message.order.depositTiming')}
                  isRequired
                  requiredText={t('form.required')}
                  placeholder={t('message.order.depositPlaceholder')}
                  width={'article'}
                />
              )}
              control={control}
              name="depositTiming"
            />
            <Button
              fullWidth
              sx={styles.primaryButtonBackgroundColor}
              onClick={() => {
                void (async () => {
                  setFirstSubmit(true);
                  await handleSubmit(onSubmit)();
                })();
              }}
              disabled={buttonDisabledFlag()}
            >
              {t('message.order.send')}
            </Button>
          </Typography>
        </Typography>
      </Modal>
    </>
  );
};

export default InsertOrderModal;
